import _, {isArray, isNumber, isObject, split, toNumber} from "lodash";
import dayjs from "dayjs";
import {localeMessages} from "@/locales/i18n";


/**
 * const for local-storage-keys
 */
export const LSK_REFRESH_JWT_OTHER = "auth-refresh-jwt-other"
export const LSK_ACCESS_JWT_OTHER = "auth-access-jwt-other"
export const LSK_REFRESH_JWT = "auth-refresh-jwt"
export const LSK_ACCESS_JWT = "auth-access-jwt"
export const STORAGE_INCLUDED_GB = 50
export const STORAGE_PRICE_PER_GB_EUR = 1


export function isDevelopment(): boolean {
    //@ts-ignore
    return !!window.webpackHotUpdate || !!window.webpackHotUpdateliftbase
}


export enum DeploymentStage {
    dev,
    testing,
    production
}

/**
 * Returns "dev", "testing", "production" based on the url eg. "app-dev.liftbase.de" vs "app-test.liftbase.de"
 * Returns null if environment could not be detemindes
 */
export function getDeploymentStage(): DeploymentStage | null {
    const host = window?.location?.host
    if (!host) {
        return null
    }

    if (host.indexOf("localhost") >= 0 || host.indexOf("app-dev") >= 0) {
        return DeploymentStage.dev
    } else if (host.indexOf("app-test") >= 0 || host.indexOf("app-demo") >= 0) {
        return DeploymentStage.testing
    } else if (host.indexOf("app.") >= 0) {
        return DeploymentStage.production
    }

    return null
}

export function isDev(): boolean {
    const host = window?.location?.host
    return host?.indexOf("localhost") >= 0 || host?.indexOf("app-dev") >= 0
}

export function isLocalhost(): boolean {
    const host = window?.location?.host
    return host?.indexOf("localhost") >= 0
}

export function isProduction(): boolean {
    return getDeploymentStage() === DeploymentStage.production
}

/**
 * Return true if:
 * - url is a relative url or
 * - url is a "liftbase.de" or subdomain
 */
export function isLiftbaseUrl(url: string) {
    try {
        const externalUrl = new URL(url)
        return externalUrl.host.indexOf("liftbase.de") >= 0;
    } catch (e) {
        // its a relative URL
        return true
    }
}

export function formatCurrencyGerman(unformatted: any): string | undefined {
    if (!unformatted) {
        return undefined
    }

    let cleared: string;
    if (typeof unformatted == "string") {
        cleared = "" + parseFormattedCurrency(unformatted)
    } else if (typeof unformatted == "number") {
        cleared = "" + unformatted
    } else {
        throw Error(unformatted + "is of unsupported type (" + typeof unformatted + "), only string | number is supported")
    }

    const valueAsNumber = Number.parseFloat(cleared)
    return valueAsNumber.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })
}

export function getIntlLocale(locale: string = 'de') {
    const normalizedLocale = `${locale || 'de'}`.trim()
    const lowerLocale = normalizedLocale.toLowerCase()

    if (lowerLocale === 'cz' || lowerLocale === 'cs' || lowerLocale === 'cs-cz') return 'cs-CZ'
    if (lowerLocale === 'de' || lowerLocale === 'de-de') return 'de-DE'
    if (lowerLocale === 'en' || lowerLocale === 'en-us') return 'en-US'
    if (lowerLocale === 'it' || lowerLocale === 'it-it') return 'it-IT'
    if (lowerLocale === 'sk' || lowerLocale === 'sk-sk') return 'sk-SK'

    return normalizedLocale
}

export function formatJsonText(value: any): string {
    if (value === null || value === undefined || value === '') return ''

    if (typeof value !== 'string') return JSON.stringify(value, null, 2)

    try {
        return JSON.stringify(JSON.parse(value), null, 2)
    } catch (e) {
        const text = value.replace(/\\n/g, '\n')
        try {
            return JSON.stringify(JSON.parse(text), null, 2)
        } catch (e2) {
            return text
        }
    }
}

export function formatCurrency(unformatted: any, locale: string = 'de', currency: string = 'EUR'): string {
    const intlLocale = getIntlLocale(locale)
    const valueAsNumber = Number(unformatted ?? 0)
    if (!Number.isFinite(valueAsNumber)) {
        return new Intl.NumberFormat(intlLocale, {style: 'currency', currency}).format(0)
    }

    return new Intl.NumberFormat(intlLocale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(valueAsNumber)
}

export function formatGigabytes(unformatted: any, locale: string = 'de'): string {
    const intlLocale = getIntlLocale(locale)
    const valueAsNumber = Number(unformatted ?? 0)
    if (!Number.isFinite(valueAsNumber)) {
        return `0 GB`
    }

    return `${valueAsNumber.toLocaleString(intlLocale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    })} GB`
}

export function parseFormattedCurrency(formatted: any): number | null {
    if (!formatted) {
        return null
    }

    if (typeof formatted == "number") {
        return formatted
    }

    return parseFloat(
        formatted
            .replace(/\./g, "")
            .replace(/,/g, ".")
    )
}

export function deepClone(obj: any) {
    return JSON.parse(JSON.stringify(obj))
}

export function deepFind(obj: any, find: (o: any) => boolean) {
    let foundObj
    try {
        JSON.stringify(obj, (key, value: any) => {
            if (find(value)) {
                foundObj = value
                throw "END"
            }
            return value
        })
    } catch (e) {
    }

    return foundObj
}

export function debounce(this: any, func: any, timeout = 600) {
    let timer: any;
    const debounced = (...args: any) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
    ;(debounced as any).cancel = () => {
        clearTimeout(timer)
        timer = null
    }
    return debounced
}

export function data(_: any) {
    return {
        data: _
    }
}

export function filterTable(dataList: [], search: string | RegExp, currentLanguage: string = 'de') {
    return dataList?.filter((data: any) => {
            return !search
                ||
                match(getTranslation(data, currentLanguage), search)
                ||
                Object.values(data)?.filter((prop: any) => {
                    if (isObject(prop)) prop = Object.values(_.omit(prop, ["__typename"]));
                    return prop && match(prop, search)
                }).length > 0
        }
    )
}

function match(text: string, search: string | RegExp) {
    if (search instanceof RegExp)
        return search.test(text)

    return text.toString().toLowerCase().includes(search.toLowerCase())
}

export function getFuzzySearchRegExp(search: string) {
    const fuzzySearch = getFuzzySearch(search)
    return new RegExp(`^${fuzzySearch}.*$`, 'i')
}

export function getFuzzySearch(search: string) {
    return search.split(" ").map((term: string) => `(?=.*${escapeRegExp(term)})`).join("")
}

const COUNTRY_DISPLAY_NAME_LOCALES = ['de', 'en', 'it', 'cs', 'sk']
const ADDRESS_STREET_TOKEN = '__street__'
const ADDRESS_NUMBER_TOKEN = '__number__'
const DEFAULT_INVOICE_ADDRESS_STREET_ALIASES = [
    'straße', 'strasse', 'str', 'str.',
    'street', 'st', 'st.', 'road', 'rd', 'rd.', 'avenue', 'ave', 'ave.', 'boulevard', 'blvd', 'blvd.', 'lane', 'ln', 'ln.', 'drive', 'dr', 'dr.', 'place', 'pl', 'pl.', 'square', 'sq', 'sq.', 'way', 'wy',
    'via', 'v.', 'viale', 'piazza', 'p.zza', 'corso', 'strada', 's.da', 'largo', 'vicolo',
    'ulice', 'ul.', 'ul', 'trida', 'třída', 'tr.', 'tř.', 'namesti', 'náměstí', 'nam.', 'nám.', 'silnice', 'sidliste', 'sídliště',
    'ulica', 'trieda', 'namestie', 'námestie', 'cesta', 'c.'
]
const DEFAULT_INVOICE_ADDRESS_NUMBER_ALIASES = [
    'nr', 'nr.', 'no', 'no.', 'number', 'nummer', 'hausnummer', 'hnr', 'hnr.', 'house number', 'hno', 'h.no',
    'numero', 'n.', 'n°', 'nº', 'civico',
    'číslo', 'cislo', 'č.', 'c.', 'č', 'c', 'popisné', 'popisne', 'orientační', 'orientacni', 'or.', 'or', 'ev.', 'ev',
    'súpisné', 'supisne', 'orientačné', 'orientacne'
]
const DEFAULT_INVOICE_ADDRESS_STREET_SUFFIX_ALIASES = ['straße', 'strasse', 'str']
let invoiceClientAddressNormalizationAliasesCache: { street: string[], number: string[] } | null = null

export type InvoiceClientAddress = {
    address?: any,
    zipcode?: any,
    city?: any,
    country?: any,
}

export function getFuzzySearchBoundaryRegExp(search: string): RegExp | null {
    const terms = `${search ?? ''}`.trim()
        .split(/\s+/)
        .map((term: string) => escapeRegExp(term))
        .filter((term: string) => !!term)

    if (!terms.length) return null

    return new RegExp(`(^|[^a-z0-9])${terms.join('[^a-z0-9]*')}(?=$|[^a-z0-9])`, 'gi')
}

export function getCountryAliasCandidates(country: string): string[] {
    const rawCountry = `${country ?? ''}`.trim()
    if (!rawCountry) return []

    const candidates = new Set<string>([rawCountry])
    const upperCountry = rawCountry.toUpperCase()

    if (/^[A-Z]{2}$/.test(upperCountry)) {
        candidates.add(upperCountry)
        candidates.add(upperCountry.toLowerCase())

        COUNTRY_DISPLAY_NAME_LOCALES.forEach((locale: string) => {
            try {
                const formatter = new Intl.DisplayNames([getIntlLocale(locale), locale], {type: 'region'})
                const displayName = formatter.of(upperCountry)
                if (displayName) candidates.add(displayName)
            } catch (e) {
            }
        })
    }

    return Array.from(candidates)
}

export function hasInvoiceClientAddressData(address: InvoiceClientAddress | null | undefined): boolean {
    return !!(address?.address || address?.zipcode || address?.city)
}

export function getInvoiceClientAddressDisplayParts(address: InvoiceClientAddress | null | undefined): string[] {
    const street = `${address?.address ?? ''}`.trim()
    const zipCity = [`${address?.zipcode ?? ''}`.trim(), `${address?.city ?? ''}`.trim()]
        .filter((value: string) => !!value)
        .join(' ')
        .trim()

    return [street, zipCity].filter((value: string) => !!value)
}

export function formatInvoiceClientAddressForText(address: InvoiceClientAddress | null | undefined): string {
    const lines = getInvoiceClientAddressDisplayParts(address)
    return lines.length ? lines.join(', ') : '-'
}

export function areInvoiceClientAddressesEquivalent(
    recognizedAddress: InvoiceClientAddress | null | undefined,
    masterAddress: InvoiceClientAddress | null | undefined
): boolean {
    if (!hasInvoiceClientAddressData(recognizedAddress)) return false

    const safeRecognizedAddress = recognizedAddress ?? {}
    const safeMasterAddress = masterAddress ?? {}
    const fullRecognizedAddress = normalizeInvoiceClientAddressText(formatInvoiceClientAddressForText(safeRecognizedAddress))
    const fullMasterAddress = normalizeInvoiceClientAddressText(formatInvoiceClientAddressForText(safeMasterAddress))
    const countryCandidates = [
        safeRecognizedAddress.country,
        safeMasterAddress.country,
    ]
    const fullRecognizedAddressWithoutCountry = stripCountryTokensFromInvoiceAddressText(fullRecognizedAddress, countryCandidates)
    const fullMasterAddressWithoutCountry = stripCountryTokensFromInvoiceAddressText(fullMasterAddress, countryCandidates)

    if (areEquivalentInvoiceAddressTexts(fullRecognizedAddress, fullMasterAddress, 0.9)) return true
    if (areEquivalentInvoiceAddressTexts(fullRecognizedAddressWithoutCountry, fullMasterAddressWithoutCountry, 0.9)) return true

    return areEquivalentInvoiceAddressFieldValues(safeRecognizedAddress.address, safeMasterAddress.address)
        && areEquivalentInvoiceAddressFieldValues(safeRecognizedAddress.zipcode, safeMasterAddress.zipcode, 'zip')
        && areEquivalentInvoiceAddressFieldValues(safeRecognizedAddress.city, safeMasterAddress.city)
}

function normalizeInvoiceClientAddressText(value: string): string {
    const rawValue = `${value ?? ''}`.trim().toLowerCase()
    if (!rawValue) return ''

    const normalized = (typeof rawValue.normalize === 'function' ? rawValue.normalize('NFD') : rawValue)
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ß/g, 'ss')
    const normalizedBase = normalized
        .replace(/[.,;:/\\()[\]{}|_]+/g, ' ')
        .replace(/[+*&'"`´~!?=]+/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

    return applyInvoiceAddressNormalizationAliases(normalizedBase)
        .replace(/\s+/g, ' ')
        .trim()
}

function normalizeInvoiceClientAddressCompact(value: string): string {
    return normalizeInvoiceClientAddressText(value).replace(/[^a-z0-9]/g, '')
}

function normalizeInvoiceClientAddressTokenKey(value: string): string {
    return normalizeInvoiceClientAddressText(value)
        .split(/\s+/)
        .filter((token: string) => !!token)
        .sort()
        .join(' ')
}

function applyInvoiceAddressNormalizationAliases(value: string): string {
    if (!value) return ''
    const aliases = getInvoiceAddressNormalizationAliases()
    let normalizedValue = `${value}`
    const sortedStreetAliases = [...aliases.street].sort((left: string, right: string) => right.length - left.length)
    const streetSuffixAliases = DEFAULT_INVOICE_ADDRESS_STREET_SUFFIX_ALIASES
        .map((alias: string) => normalizeInvoiceAddressAliasToken(alias))
        .filter((alias: string) => !!alias)
        .sort((left: string, right: string) => right.length - left.length)

    sortedStreetAliases.forEach((alias: string) => {
        normalizedValue = replaceWholeInvoiceAddressToken(normalizedValue, alias, ADDRESS_STREET_TOKEN)
    })
    streetSuffixAliases.forEach((alias: string) => {
        normalizedValue = replaceInvoiceAddressTokenSuffix(normalizedValue, alias, ADDRESS_STREET_TOKEN)
    })
    aliases.number.forEach((alias: string) => {
        normalizedValue = replaceWholeInvoiceAddressToken(normalizedValue, alias, ADDRESS_NUMBER_TOKEN)
    })

    return normalizedValue
}

function getInvoiceAddressNormalizationAliases() {
    if (invoiceClientAddressNormalizationAliasesCache) return invoiceClientAddressNormalizationAliasesCache

    const streetAliases = new Set<string>([ADDRESS_STREET_TOKEN])
    const numberAliases = new Set<string>([ADDRESS_NUMBER_TOKEN])

    DEFAULT_INVOICE_ADDRESS_STREET_ALIASES.forEach((alias: string) => {
        streetAliases.add(normalizeInvoiceAddressAliasToken(alias))
    })
    DEFAULT_INVOICE_ADDRESS_NUMBER_ALIASES.forEach((alias: string) => {
        numberAliases.add(normalizeInvoiceAddressAliasToken(alias))
    })

    Object.values(localeMessages ?? {}).forEach((localeMessage: any) => {
        const localeConfig = localeMessage?.invoice?.page?.client_address_check?.address_normalization
        readInvoiceAddressNormalizationAliasList(localeConfig?.street_aliases).forEach((alias: string) => {
            streetAliases.add(alias)
        })
        readInvoiceAddressNormalizationAliasList(localeConfig?.number_aliases).forEach((alias: string) => {
            numberAliases.add(alias)
        })
    })

    invoiceClientAddressNormalizationAliasesCache = {
        street: Array.from(streetAliases),
        number: Array.from(numberAliases),
    }
    return invoiceClientAddressNormalizationAliasesCache
}

function readInvoiceAddressNormalizationAliasList(list: any): string[] {
    if (!Array.isArray(list)) return []
    return list
        .map((entry: any) => normalizeInvoiceAddressAliasToken(entry))
        .filter((entry: string) => !!entry)
}

function normalizeInvoiceAddressAliasToken(value: any): string {
    const rawValue = `${value ?? ''}`.trim().toLowerCase()
    if (!rawValue) return ''

    const normalized = (typeof rawValue.normalize === 'function' ? rawValue.normalize('NFD') : rawValue)
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/[.,;:/\\()[\]{}|_]+/g, ' ')
        .replace(/[+*&'"`´~!?=]+/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

    return normalized || ''
}

function replaceWholeInvoiceAddressToken(input: string, alias: string, replacement: string): string {
    if (!input || !alias) return input

    const escapedAlias = escapeRegExp(alias).replace(/\s+/g, '\\s+')
    const regex = new RegExp(`(^|[^a-z0-9])${escapedAlias}(?=$|[^a-z0-9])`, 'g')
    return input.replace(regex, `$1${replacement}`)
}

function replaceInvoiceAddressTokenSuffix(input: string, alias: string, replacement: string): string {
    if (!input || !alias || alias.includes(' ') || alias.length < 3) return input

    const escapedAlias = escapeRegExp(alias)
    const regex = new RegExp(`(^|[^a-z0-9])([a-z0-9]{2,})${escapedAlias}(?=$|[^a-z0-9])`, 'g')
    return input.replace(regex, (_match: string, prefix: string, root: string) => `${prefix}${root} ${replacement}`)
}

function stripCountryTokensFromInvoiceAddressText(value: string, countries: string[] = []): string {
    if (!value) return ''

    let normalizedValue = `${value}`
    const normalizedCandidates = Array.from(new Set(
        countries
            .flatMap((country: string) => getCountryAliasCandidates(country))
            .map((candidate: string) => normalizeInvoiceClientAddressText(candidate))
            .filter((candidate: string) => !!candidate)
    ))

    normalizedCandidates.forEach((candidate: string) => {
        const fuzzyRegex = getFuzzySearchBoundaryRegExp(candidate)
        if (!fuzzyRegex) return
        normalizedValue = normalizedValue.replace(fuzzyRegex, '$1 ')
    })

    return normalizedValue.replace(/\s+/g, ' ').trim()
}

function normalizeInvoiceAddressZipValue(value: string): string {
    return normalizeInvoiceClientAddressText(value).replace(/[^a-z0-9]/g, '')
}

function areEquivalentInvoiceAddressFieldValues(recognizedValue: string, masterValue: string, type: 'text' | 'zip' = 'text'): boolean {
    if (type === 'zip') {
        const normalizedRecognized = normalizeInvoiceAddressZipValue(recognizedValue)
        const normalizedMaster = normalizeInvoiceAddressZipValue(masterValue)
        if (!normalizedRecognized) return true
        if (!normalizedMaster) return false
        return normalizedRecognized === normalizedMaster
    }

    const normalizedRecognized = normalizeInvoiceClientAddressText(recognizedValue)
    const normalizedMaster = normalizeInvoiceClientAddressText(masterValue)
    if (!normalizedRecognized) return true
    if (!normalizedMaster) return false

    return areEquivalentInvoiceAddressTexts(normalizedRecognized, normalizedMaster, 0.86)
}

function areSimilarInvoiceAddressTexts(leftValue: string, rightValue: string, threshold: number = 0.88): boolean {
    const left = normalizeInvoiceClientAddressCompact(leftValue)
    const right = normalizeInvoiceClientAddressCompact(rightValue)
    if (!left || !right) return false
    if (left === right) return true

    const minLength = Math.min(left.length, right.length)
    const maxLength = Math.max(left.length, right.length)
    if (minLength >= 6 && (left.includes(right) || right.includes(left)) && (minLength / maxLength) >= 0.84) {
        return true
    }

    return getInvoiceAddressSimilarity(left, right) >= threshold
}

function areEquivalentInvoiceAddressTexts(leftValue: string, rightValue: string, threshold: number = 0.88): boolean {
    const left = normalizeInvoiceClientAddressText(leftValue)
    const right = normalizeInvoiceClientAddressText(rightValue)
    if (!left || !right) return false
    if (left === right) return true
    if (normalizeInvoiceClientAddressTokenKey(left) === normalizeInvoiceClientAddressTokenKey(right)) return true
    return areSimilarInvoiceAddressTexts(left, right, threshold)
}

function getInvoiceAddressSimilarity(leftValue: string, rightValue: string): number {
    const left = `${leftValue ?? ''}`
    const right = `${rightValue ?? ''}`
    if (!left && !right) return 1
    if (!left || !right) return 0

    const leftLength = left.length
    const rightLength = right.length
    const matrix = Array.from({length: leftLength + 1}, () => new Array(rightLength + 1).fill(0))

    for (let index = 0; index <= leftLength; index += 1) matrix[index][0] = index
    for (let index = 0; index <= rightLength; index += 1) matrix[0][index] = index

    for (let leftIndex = 1; leftIndex <= leftLength; leftIndex += 1) {
        for (let rightIndex = 1; rightIndex <= rightLength; rightIndex += 1) {
            const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1
            matrix[leftIndex][rightIndex] = Math.min(
                matrix[leftIndex - 1][rightIndex] + 1,
                matrix[leftIndex][rightIndex - 1] + 1,
                matrix[leftIndex - 1][rightIndex - 1] + cost
            )
        }
    }

    const maxLength = Math.max(leftLength, rightLength)
    return maxLength > 0 ? (maxLength - matrix[leftLength][rightLength]) / maxLength : 1
}


function getTranslation(item: any, currentLanguage: string, listName: string = 'translations', fieldName: string = 'translation') {
    if (!item) return ''
    return item[listName]?.length > 0 ?
        (item[listName].find((item: any) => item.language == currentLanguage) ?? item[listName][0])[fieldName] :
        item[fieldName] ?? item?.name
}


export function getValidationErrorsFromDataTable(dataTable: any, columns: any) {
    let validationErrors = []
    const notEmptyRow = dataTable.filter((item: any) => !isEmptyRow(item))
    validationErrors = notEmptyRow.map((row: any, index: number) => {
        let errors = columns.filter((column: any) => {
            if(!column.required) return false
            const emptyValue = ['', null, undefined]
            if (!!column.fnValue && typeof column.fnValue == "function") {
                return emptyValue.includes(column.fnValue(row))
            }
            else return  ['', null, undefined].includes(row[column.name])
        })
        return errors.length > 0 ? {row: index, columns: errors, type: 'required'} : null

    }).filter((errorRow: any) => !!errorRow) || []

    const uniqueColumns = columns.filter((column: any) => column.unique)
    uniqueColumns?.forEach((column: any) => {
        let rowIndexErrors = notEmptyRow?.map((rowA: any, indexA: number) => {
            const duplicated = notEmptyRow.filter((rowB: any, indexB: number) => {
                return !!rowA[column.name] && rowA[column.name] == rowB[column.name] && indexA != indexB
            })
            const violateUniqueKey = duplicated.length > 0
            return violateUniqueKey ? indexA : null
        })?.filter((indexError: any) => isNumber(indexError))
        rowIndexErrors?.forEach((rowIndexError: number) => {
            validationErrors.push({row: rowIndexError, columns: [column], type: 'unique'})
        })
    })

    const customValidateColumns = columns.filter((column: any) => !!column.validate && typeof column.validate == "function")
    customValidateColumns?.forEach((column: any) => {
        let rowIndexErrors = notEmptyRow?.map((rowA: any, indexA: number) => {
            try {
                return !column.validate(rowA[column.name], rowA, columns) ? indexA : null
            } catch (e) {
                return indexA
            }
        })?.filter((indexError: any) => isNumber(indexError))
        rowIndexErrors?.forEach((rowIndexError: number) => {
            validationErrors.push({row: rowIndexError, columns: [column], type: 'invalid'})
        })
    })

    return validationErrors
}

export function formatDataTableBeforeSave(dataTable: any, columns: any, table: string = '', language: string = 'de') {
    return dataTable
        .filter((item: any) => !isEmptyRow(item))
        .map((item: any) => {
            columns.map((column: any) => {

                // Handle numeric fields with mask
                if (column.type == 'numeric' && !!column.mask) {
                    const rawValue = item[column.name]?.toString().trim() ?? ''
                    // Detect if the original value contains a negative sign (any position)
                    const isNegative = rawValue.includes('-')
                    // Extract numeric part (remove currency symbols, spaces, letters)
                    const numericPart = rawValue
                        .replace(/[^0-9,\.]+/g, '')
                        .replace(/\./g, '')   // remove thousand separators
                        .replace(',', '.')   // convert decimal comma to dot

                    if (column.required === false) {
                        item[column.name] =
                            (!numericPart && numericPart !== '0') || numericPart === ''
                                ? null
                                : toNumber(isNegative ? `-${numericPart}` : numericPart)
                    } else {
                        const value = numericPart
                        item[column.name] = toNumber(isNegative ? `-${value}` : value)
                    }
                }

                // Convert numeric, dropdown, and autocomplete fields without mask
                if (
                    ['numeric', 'dropdown', 'autocomplete'].includes(column.type) &&
                    !column.multiple &&
                    !column.mask &&
                    column.name !== 'currency' &&
                    column.subtype !== 'text'
                ) {
                    const value = column.type === 'numeric' && column.decimal
                        ? item[column.name]?.toString().replace(column.decimal, '.')
                        : item[column.name]
                    item[column.name] = !!value ? toNumber(value) : null
                }

                // Convert 0 values to null for dropdown/autocomplete
                if ((column.type == 'dropdown' || column.type == 'autocomplete') && item[column.name] == 0) {
                    item[column.name] = null
                }

                // Remove ID field if empty
                if (column.name == 'id' && !item[column.name]) {
                    delete item[column.name]
                }

                // For calendar fields, keep only the date part (remove time)
                if (column.type == 'calendar') {
                    if (!!item[column.name]) {
                        item[column.name] = split(item[column.name], ' ')[0]
                    } else {
                        delete item[column.name]
                    }
                }

                // Handle i18n translation fields
                if (column.i18n) {
                    if (!item.id || !isNumber(item.id)) {
                        item.translations = {}
                        item.translations.data = {
                            table,
                            language,
                            field: column.name,
                            translation: item[column.name]
                        }
                    }
                }

                // Handle multiple (relation) fields
                if (column.multiple) {
                    if (!item.id || !isNumber(item.id)) {
                        item[column.relation] = {}
                        item[column.relation].data =
                            item[column.name]
                                ?.split(';')
                                ?.filter((i: any) => ![undefined, null, '', '\u0000'].includes(i))
                                ?.map((i: any) => {
                                    const obj: any = {}
                                    obj[column.name] = isNaN(toNumber(i)) ? i : toNumber(i)
                                    return obj
                                }) ?? []
                        delete item[column.name]
                    }
                }

                // Apply custom value function if defined
                if (!!column.fnValue && typeof column.fnValue == "function") {
                    item[column.name] = column.fnValue(item)
                }
                // Remove hidden fields if empty or not a number
                else if (column.type == "hidden" && (!item[column.name] || !isNumber(item[column.name]))) {
                    delete item[column.name]
                }

                // Normalize email fields: lowercase and remove spaces
                if (column.type == 'email') {
                    item[column.name] = item[column.name]?.toLowerCase()?.replaceAll(/\s/g, "")
                }

                // Apply default value if not set
                if (!!column.default && !item[column.name]) {
                    item[column.name] = column.default
                }

            })
            return item
        })
}


export function getRelationsToSave(dataTable: any, columns: any, table: string = '', language: string = 'de') {
    let relations = {} as any
    dataTable.filter((item: any) => !!item.id)?.forEach((row: any) => {
        columns.filter((column: any) => column.multiple)?.map((column: any) => {
            if (!isArray(relations[column.relation])) relations[column.relation] = []
            _.uniq(row[column.name]?.split(';')?.filter((item: any) => item != ''))
                ?.map((item: any) => {
                    let obj = {} as any
                    obj[column.name] = isNaN(toNumber(item)) ? item : toNumber(item)
                    obj[column.foreign_key] = toNumber(row.id)
                    relations[column.relation].push(obj)
                })

            delete row[column.name]
        })
        columns.filter((column: any) => column.i18n)?.map((column: any) => {
            if (!isArray(relations.translations)) relations.translations = []
            relations.translations.push({
                foreign_key: row.id,
                table,
                language,
                field: column.name,
                translation: row[column.name]
            })
            delete row[column.name]
        })
    })
    return relations
}

export function isEmptyRow(item: any) {
    return !Object.keys(item).find((key: string) => !!item[key] && item[key] != '0')
}

export function getRemovedIdsFromDataTable(dataTableBefore: any, dataTableAfter: any) {
    let idsBefore = dataTableBefore.filter((item: any) => !item.ms365_imported)?.map((item: any) => {
        return item.id
    })
    let idsAfter = dataTableAfter.filter((item: any) => !!item.id).map((item: any) => {
        return item.id
    }) ?? []

    return idsBefore.filter((id: number) => !idsAfter.includes(id));
}


/**
 * Returns the amount of hours between to dates
 */
export function hoursBetween(date1: Date, date2: Date) {
    // thank you https://stackoverflow.com/a/19225540/808723

    //@ts-ignore
    return Math.floor((date1 - date2) / 36e5);
}

export function convertParamsToObject(url: string) {
    let queryParams = new URLSearchParams(url).entries()
    const result = {} as any;
    for (const [key, value] of queryParams) {
        result[key] = value;
    }
    return result;
}

export function slugify(text: string) {
    return text.toString().toLowerCase()
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\u0100-\uFFFF\w\-]/g, '-') // Remove all non-word chars ( fix for UTF-8 chars )
        .replace(/\-\-+/g, '-')         // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
}



export function normalizeVatNumber(input: any) {
    return input?.toString()
        .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
        .replace(/[\u00AD\u200B-\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g, '') ?? input
}

export function isValidVatNumber(input: any) {
    return /^[0-9a-zA-Z. _]{1,15}$/.test(input)
}

export function isValidBIC(input: any) {
    return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(input)
}

export function isValidIBANNumber(input: any) {
    const CODE_LENGTHS: any = {
        AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
        CH: 21, CR: 21, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
        FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
        HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
        LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
        MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
        RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26,
        AL: 28, BY: 28, EG: 29, GE: 22, IQ: 23, LC: 32, SC: 31, ST: 25,
        SV: 28, TL: 23, UA: 29, VA: 22, VG: 24, XK: 20
    };
    let iban = String(input).toUpperCase().replace(/[^A-Z0-9]/g, ''), // keep only alphanumeric characters
        code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/), // match and capture (1) the country code, (2) the check digits, and (3) the rest
        digits;
    // check syntax and length
    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
        return false;
    }
    // rearrange country code and check digits, and convert chars to ints
    digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) => {
        return (letter.charCodeAt(0) - 55).toString();
    });
    // final check
    return mod97(digits);
}

function mod97(string: any) {
    let checksum = string.slice(0, 2), fragment;
    for (let offset = 2; offset < string.length; offset += 7) {
        fragment = String(checksum) + string.substring(offset, offset + 7);
        checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
}

export function capitalizeFirstLetter(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

export function checkIfStringIsValidISODate(str: any) {
    return (typeof str == "string" && /^\d{4}-\d{2}-\d{2}$/.test(str.substring(0, 10)) && dayjs(str).isValid())
}


export function compareTwoStrings(first: any, second: any) {
    first = first?.replace(/\s+/g, '')
    second = second?.replace(/\s+/g, '')

    if (!first || !second) return 0
    if (first === second) return 1; // identical or empty
    if (first.length < 2 || second.length < 2) return 0; // if either is a 0-letter or 1-letter string

    let firstBigrams = new Map();
    for (let i = 0; i < first.length - 1; i++) {
        const bigram = first.substring(i, i + 2);
        const count = firstBigrams.has(bigram)
            ? firstBigrams.get(bigram) + 1
            : 1;

        firstBigrams.set(bigram, count);
    }


    let intersectionSize = 0;
    for (let i = 0; i < second.length - 1; i++) {
        const bigram = second.substring(i, i + 2);
        const count = firstBigrams.has(bigram)
            ? firstBigrams.get(bigram)
            : 0;

        if (count > 0) {
            firstBigrams.set(bigram, count - 1);
            intersectionSize++;
        }
    }

    return (2.0 * intersectionSize) / (first.length + second.length - 2);
}

export function findBestMatch(mainString: any, targetStrings: any) {
    if (!areArgsValid(mainString, targetStrings)) throw new Error('Bad arguments: First argument should be a string, second should be an array of strings');

    const ratings = [];
    let bestMatchIndex = 0;

    for (let i = 0; i < targetStrings.length; i++) {
        const currentTargetString = targetStrings[i];
        const currentRating = compareTwoStrings(mainString, currentTargetString)
        ratings.push({target: currentTargetString, rating: currentRating})
        if (currentRating > ratings[bestMatchIndex].rating) {
            bestMatchIndex = i
        }
    }


    const bestMatch = ratings[bestMatchIndex]

    return {ratings: ratings, bestMatch: bestMatch, bestMatchIndex: bestMatchIndex};
}

function areArgsValid(mainString: any, targetStrings: any) {
    if (typeof mainString !== 'string') return false;
    if (!Array.isArray(targetStrings)) return false;
    if (!targetStrings.length) return false;
    if (targetStrings.find(function (s) {
        return typeof s !== 'string'
    })) return false;
    return true;
}

export function sortDictionaryOnKeys(dict: any, nullFirst: boolean = true) {

    let sorted = []

    for (let key in dict) sorted[sorted.length] = key
    sorted.sort()

    let tempDict = {} as any
    if (nullFirst && !!dict.null) tempDict.null = dict.null

    for (let i = 0; i < sorted.length; i++) {
        if (nullFirst && sorted[i] === 'null') continue
        tempDict[sorted[i]] = dict[sorted[i]]
    }

    return tempDict
}

export function trimObjectProperties(obj: any) {
    let trimmedObject: any = {};

    for (let prop in obj) {
        if (obj.hasOwnProperty(prop)) {
            if (typeof obj[prop] === 'string') {
                trimmedObject[prop] = obj[prop].trim()
            } else {
                trimmedObject[prop] = obj[prop]
            }
        }
    }

    return trimmedObject
}


export function escapeRegExp(text?: string) {
    return text?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') ?? ''
}

export function omitWarning(action: any = {}) {
    // Store the original console methods
    const originalWarn = console.warn
    try {
        // Temporarily replace console methods to suppress warnings and errors
        console.warn = () => {
        }
        // Execute the action and return its result
        return action()
    } catch (e) {
        // Return null if an exception occurs
        return null
    } finally {
        // Restore the original console methods
        console.warn = originalWarn
    }
}

export const hasuraErrorMap: {
    pattern: RegExp;
    key: string;
    hasField?: boolean;
    hasValue?: boolean;
    hasType?: boolean
}[] = [
    {
        pattern: /field '(.*?)' not found in type: '(.*?)'/,
        key: 'errors.specific_field_not_found',
        hasField: true,
        hasValue: true
    },

    {
        pattern: /unexpected null value for type '(.*?)'/,
        key: 'errors.unexpected_null_value_for_type',
        hasType: true
    },

    {
        pattern: /expected (a|an) (.*?) for type '(.*?)', but found (.*?)/,
        key: 'errors.expected_type_but_found',
        hasField: true,
        hasValue: true
    },

    {
        pattern: /duplicate key value violates unique constraint "(.*?)"/,
        key: 'errors.duplicate_key_violation',
        hasField: true
    },

    // NOT NULL violation
    {
        pattern: /null value in column "(.*?)" violates not-null constraint/,
        key: 'errors.specific_field_required',
        hasField: true
    },

    // Invalid type (e.g., for integer, uuid, etc.)
    {
        pattern: /invalid input syntax for type (.*?): "(.*?)"/,
        key: 'errors.specific_invalid_type',
        hasValue: true
    },

    // Unique constraint violation
    {
        pattern: /duplicate key value violates unique constraint "(.*?)"/,
        key: 'errors.unique_violation' // generic by default (field name is not reliably extractable)
    },

    // Foreign key violation
    {
        pattern: /insert or update on table "(.*?)" violates foreign key constraint "(.*?)"/,
        key: 'errors.foreign_key_violation'
    },

    // Permission denied
    {
        pattern: /permission denied for table "(.*?)"/,
        key: 'errors.permission_denied'
    },

    // Check constraint violation
    {
        pattern: /new row for relation "(.*?)" violates check constraint "(.*?)"/,
        key: 'errors.check_violation'
    },

    // Value too long for character varying
    {
        pattern: /value too long for type character varying/,
        key: 'errors.value_too_long'
    },

    // Fallback
    {
        pattern: /.*/,
        key: 'errors.unexpected'
    }
]

export function getHasuraErrorKeyAndParams(message: string) {
    for (const {pattern, key, hasField, hasValue, hasType} of hasuraErrorMap) {
        const match = message.match(pattern)

        if (match) {
            const params: { [key: string]: string } = {}

            if (hasField) {
                params.field = match[1]
            }
            if (hasValue) {
                params.value = match[2]
            }
            if (hasType) {
                params.type = match[1]
            }

            return {
                key,
                params,
            };
        }
    }

    // Fallback
    return {
        key: 'errors.unexpected',
        params: {},
    };
}
